import dayjs from 'dayjs';

import type { VesselFormValues } from '../../schedule/components/RHFVesselForm';

import type { ShipGroup, VoyageRow } from './groupEditTypes';

// ── Transformer ──────────────────────────────────────────────

/**
 * Converts the ShipGroup object (from the backend's VoyageGroupDTO)
 * into the editData shape expected by RHFVesselForm.
 */
export const transformShipGroupToEditData = (
  shipGroup: ShipGroup
): Partial<VesselFormValues> => {
  const destEntries = Object.values(shipGroup.destinations);

  // Emit one form destination entry per voyage (not per port) so that
  // voyages sharing the same destination port each get their own block.
  const destinations = destEntries.flatMap((dest) => {
    return dest.voyages.map((voyage) => {
      const shipments = (voyage.shipments ?? []).map((s) => ({
        _shipmentId: s.id || undefined,
        id: s.id || undefined,
        market: s.market?.id ?? null,
        firstShippingLineOffer: s.firstShippingLineOffer ?? 0,
        adjustedPORequest: s.adjustedPORequest ?? 0,
        finalAgreement: s.finalAgreement ?? 0,
        effectiveShipped: s.effectiveShipped ?? 0,
      }));

      return {
        // Tracking fields preserved through the form for diff-based update logic.
        _voyageId: voyage.id != null ? String(voyage.id) : undefined,
        _voyageNumber: voyage.voyageNumber ?? undefined,
        _cancelled: voyage.cancelled ?? false,
        destinationCountry: dest.port?.country?.countryCode ?? '',
        destinationPort: dest.port?.unLocode ?? '',
        actualETA:
          (voyage.actualETA ?? dest.actualETA)
            ? dayjs(voyage.actualETA ?? dest.actualETA)
            : null,
        generalRemarks: voyage.generalRemarks ?? '',
        shipments:
          shipments.length > 0
            ? shipments
            : [
                {
                  id: undefined,
                  market: null,
                  firstShippingLineOffer: 0,
                  adjustedPORequest: 0,
                  finalAgreement: 0,
                  effectiveShipped: 0,
                },
              ],
      };
    });
  });

  // Sort destination blocks by Latest ETA ascending
  destinations.sort((a, b) => {
    const etaA = a.actualETA ? a.actualETA.valueOf() : 0;
    const etaB = b.actualETA ? b.actualETA.valueOf() : 0;
    return etaA - etaB;
  });

  // Use the first voyage for shared fields that aren't on the group itself
  const firstVoyage = destEntries[0]?.voyages[0];

  return {
    imo: shipGroup.ship?.imo ?? '',
    vessel: shipGroup.ship?.name ?? '',
    departureCountry: shipGroup.departurePort?.country?.countryCode ?? '',
    departurePort: shipGroup.departurePort?.unLocode ?? '',
    portOperator: shipGroup.portOperator?.supplierNo ?? ('' as any),
    shippingCompany: shipGroup.ship?.supplier?.supplierNo ?? ('' as any),
    carrier: shipGroup.carrier?.supplierNo ?? ('' as any),
    actualETD: shipGroup.etdDate ? dayjs(shipGroup.etdDate) : (null as any),
    slotConfirmed: firstVoyage?.slotConfirmed ?? false,
    voyageNumber: firstVoyage?.voyageNumber ?? '',
    destinations: destinations as any,
  };
};

// ── Group param derivation ────────────────────────────────────

export interface GroupParams {
  imo: string;
  departurePort: string;
  portOperator: string;
  etdDate: string;
  depCountry: string;
}

/**
 * Derives the four params needed for getVoyageGroup from either
 * a pre-grouped shipGroup or a single voyageRow.
 * Returns null if required fields are missing.
 */
export const deriveGroupParams = (
  shipGroup: ShipGroup | null | undefined,
  voyageRow: VoyageRow | null | undefined
): GroupParams | null => {
  if (shipGroup) {
    return {
      imo: shipGroup.ship?.imo,
      departurePort: shipGroup.departurePort?.unLocode,
      portOperator: String(shipGroup.portOperator?.supplierNo),
      etdDate: shipGroup.etdDate
        ? dayjs(shipGroup.etdDate).format('YYYY-MM-DD')
        : '',
      depCountry: shipGroup.departurePort?.country?.countryCode ?? '',
    } as GroupParams;
  }

  if (voyageRow) {
    const dp = voyageRow.departurePort;
    const po = voyageRow.portOperator;
    const dc = voyageRow.departureCountry;
    const imo = voyageRow.ship?.imo ?? voyageRow.shipImo;
    const depPort = typeof dp === 'string' ? dp : dp?.unLocode;
    const portOp =
      typeof po === 'object' && po !== null
        ? (po.supplierNo ?? po.id)
        : (po ?? voyageRow.portOperatorId);
    const etd = voyageRow.actualETD ?? voyageRow.actualEtd;
    const depCountry =
      (typeof dp === 'object' ? dp?.country?.countryCode : undefined) ??
      (typeof dc === 'string' ? dc : dc?.countryCode) ??
      '';

    if (!imo || !depPort || !portOp || !etd) {
      // eslint-disable-next-line no-console
      console.error(
        '[VoyageGroupEditModal] Missing group params from voyageRow:',
        { imo, depPort, portOp, etd, voyageRow }
      );
      return null;
    }

    return {
      imo,
      departurePort: depPort,
      portOperator: String(portOp),
      etdDate: dayjs(etd).format('YYYY-MM-DD'),
      depCountry,
    };
  }

  return null;
};

// ── Dirty-check helpers ───────────────────────────────────────

export const formatDT = (v: any): string | null =>
  v ? dayjs(v).format('YYYY-MM-DDTHH:mm:ss') : null;

export const formatDateOnly = (v: any): string | null =>
  v ? dayjs(v).format('YYYY-MM-DD') : null;

export const isDestDirty = (dest: any, baseline: any): boolean => {
  if (!baseline) return true;

  if (dest.destinationCountry !== baseline.destinationCountry) return true;
  if (dest.destinationPort !== baseline.destinationPort) return true;
  if (formatDateOnly(dest.actualETA) !== formatDateOnly(baseline.actualETA))
    return true;
  if ((dest.generalRemarks ?? '') !== (baseline.generalRemarks ?? ''))
    return true;

  const destShipments: any[] = dest.shipments ?? [];
  const baseShipments: any[] = baseline.shipments ?? [];

  if (destShipments.length !== baseShipments.length) return true;

  for (let i = 0; i < destShipments.length; i++) {
    const s = destShipments[i];
    const bs = baseShipments[i];

    if (!bs) return true;
    if (s.market !== bs.market) return true;
    if ((s.firstShippingLineOffer ?? 0) !== (bs.firstShippingLineOffer ?? 0))
      return true;
    if ((s.adjustedPORequest ?? 0) !== (bs.adjustedPORequest ?? 0)) return true;
    if ((s.finalAgreement ?? 0) !== (bs.finalAgreement ?? 0)) return true;
    if ((s.effectiveShipped ?? 0) !== (bs.effectiveShipped ?? 0)) return true;
  }

  return false;
};
